import type { CreateShareLinkBody } from "@/apis/bacchus/shareTypes";
import type { MarketSizingShareConfig } from "./MarketSizingContent";

type ShareConfigBody = CreateShareLinkBody["config"];

export const toShareConfigBody = (
  config: MarketSizingShareConfig,
  localizeInstalls: boolean,
  scoringV2Enabled: boolean
): ShareConfigBody => ({
  spend_categories_by_model: config.spendCategoriesByModel,
  tier_settings_by_model: config.tierSettingsByModel,
  corporate_parent_only: config.corporateParentOnly,
  localize_installs: localizeInstalls,
  scoring_v2_enabled: scoringV2Enabled,
});

const canonical = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(canonical);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([key, v]) => [key, canonical(v)])
    );
  }
  return value;
};

/** Compares only the fields a share freezes; the server adds bookkeeping
 * (e.g. a config version) that must not count as drift. */
export const hasShareConfigDrifted = (
  frozen: ShareConfigBody,
  current: ShareConfigBody
): boolean => {
  // Cleared selections and untouched tier settings look identical to prospects.
  const pick = (c: ShareConfigBody) =>
    canonical({
      spend_categories_by_model: Object.fromEntries(
        Object.entries(c.spend_categories_by_model).filter(
          ([, selection]) => selection.ids.length > 0
        )
      ),
      tier_settings_by_model: Object.fromEntries(
        Object.entries(c.tier_settings_by_model ?? {})
          .map(([model, settings]) => {
            const normalized = {
              names: Object.fromEntries(
                Object.entries(settings.names).filter(
                  ([, name]) => name.trim() !== ""
                )
              ),
              hidden: [...settings.hidden].sort(),
            };
            return [model, normalized] as const;
          })
          .filter(
            ([, settings]) =>
              Object.keys(settings.names).length > 0 ||
              settings.hidden.length > 0
          )
      ),
      corporate_parent_only: c.corporate_parent_only,
      localize_installs: c.localize_installs,
      scoring_v2_enabled: c.scoring_v2_enabled,
    });
  return JSON.stringify(pick(frozen)) !== JSON.stringify(pick(current));
};
